import { dayjs, formatDate } from '@koob/dates';

const partWidths = (format: string): number[] =>
  format
    .split(/[^A-Za-z]+/)
    .filter(Boolean)
    .map((part) => part.length);

const partSeparator = (format: string): string =>
  format.replace(/[A-Za-z]/g, '')[0] ?? '/';

// Expanded here, never parsed as 'YY': the parser's own sliding window would
// turn 50 into 2050 where KOOB wants 1950.
const expandYear = (year: string): string =>
  year.length > 2 ? year : `${Number(year) <= 49 ? '20' : '19'}${year}`;

/** Inserts the format's separators as the user types: '17082026' → '17/08/2026'. */
export const maskDateTyping = (
  previous: string,
  next: string,
  format: string,
): string => {
  const widths = partWidths(format);
  const separator = partSeparator(format);
  const total = widths.reduce((sum, width) => sum + width, 0);
  const digits = next.replace(/\D/g, '').slice(0, total);

  const groups: string[] = [];
  let offset = 0;
  for (const width of widths) {
    if (offset >= digits.length) break;
    groups.push(digits.slice(offset, offset + width));
    offset += width;
  }

  const masked = groups.join(separator);
  const onGroupBoundary =
    digits.length > 0 && digits.length < total && offset === digits.length;

  // Only while typing: re-adding it on a backspace would make the separator
  // impossible to delete through.
  return onGroupBoundary && next.length >= previous.length
    ? masked + separator
    : masked;
};

/**
 * Splits a value into its parts. With separators they are authoritative, so a
 * one-digit day or month is padded instead of being re-cut into a different
 * date; without them the format's widths do the cutting.
 */
const splitParts = (text: string, widths: number[]): string[] | null => {
  const leading = widths.slice(0, -1);
  const yearWidth = widths[widths.length - 1];

  if (/\D/.test(text)) {
    const parts = text.split(/[\s/.-]+/).filter(Boolean);
    if (parts.length !== widths.length) return null;
    if (parts.some((part) => !/^\d+$/.test(part))) return null;
    if (leading.some((width, index) => parts[index].length > width))
      return null;
    const year = parts[parts.length - 1];
    if (year.length !== 2 && year.length !== yearWidth) return null;
    return [
      ...leading.map((width, index) => parts[index].padStart(width, '0')),
      year,
    ];
  }

  const leadingWidth = leading.reduce((sum, width) => sum + width, 0);
  if (
    text.length !== leadingWidth + 2 &&
    text.length !== leadingWidth + yearWidth
  ) {
    return null;
  }
  const parts: string[] = [];
  let offset = 0;
  for (const width of leading) {
    parts.push(text.slice(offset, offset + width));
    offset += width;
  }
  parts.push(text.slice(offset));
  return parts;
};

/** Separators optional, year on 2 or 4 digits, either component order. */
export const parseFlexibleDate = (
  text: string,
  format: string,
): Date | null => {
  const trimmed = text.trim();
  if (!trimmed) return null;

  const widths = partWidths(format);
  const parts = splitParts(trimmed, widths);
  if (!parts) return null;

  const leading = parts.slice(0, -1);
  // Over 12 means it is the day; an ambiguous pair stays KOOB's day-first.
  if (
    leading.length === 2 &&
    Number(leading[0]) <= 12 &&
    Number(leading[1]) > 12
  ) {
    leading.reverse();
  }

  const normalized = [...leading, expandYear(parts[parts.length - 1])].join(
    partSeparator(format),
  );
  const parsed = dayjs(normalized, format, true);
  return parsed.isValid() ? parsed.toDate() : null;
};

/**
 * Parses "from → to". A dash can separate the two dates and the parts of each
 * ("20-09-2026-25-09-2026"), so every "→", "-" or "–" is tried as the cut and
 * the one leaving two valid dates wins. No cut, or more than one, means the
 * value is not read as a period rather than guessed at.
 */
export const parseRangeText = (
  text: string,
  format: string,
): [Date, Date] | null => {
  const ranges: [Date, Date][] = [];
  for (let index = 0; index < text.length; index += 1) {
    if (!'→-–'.includes(text[index])) continue;
    const from = parseFlexibleDate(text.slice(0, index), format);
    const to = parseFlexibleDate(text.slice(index + 1), format);
    if (from && to) ranges.push([from, to]);
  }
  return ranges.length === 1 ? ranges[0] : null;
};

export const normalizePastedDate = (pasted: string, format: string): string => {
  const parsed = parseFlexibleDate(pasted, format);
  return parsed ? formatDate(parsed, format) : pasted;
};

export const normalizePastedRange = (
  pasted: string,
  format: string,
): string => {
  const range = parseRangeText(pasted, format);
  if (!range) return pasted;
  return `${formatDate(range[0], format)} → ${formatDate(range[1], format)}`;
};

/**
 * A month field's value stands for the whole month, like the month picker's own
 * disabled rule — so it is not rejected for starting before minDate.
 */
export const isWithinBounds = (
  date: Date,
  format: string,
  minDate?: Date,
  maxDate?: Date,
): boolean => {
  const unit = format.includes('D') ? 'day' : 'month';
  const value = dayjs(date);
  if (minDate && value.endOf(unit).isBefore(minDate, 'day')) return false;
  if (maxDate && value.startOf(unit).isAfter(maxDate, 'day')) return false;
  return true;
};
